import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { AlertFactory } from 'alerts/Alert'
import { Id } from 'types/definitions'
import ModalViewModel, {
  ModalViewModelClosingSignal,
} from 'infra/ModalViewModel'

/**
 * A participant the note's author is permitted to notify.
 */
export interface JobNoteRecipient {
  userPublicId: Id
  name: string
  role: 'RM' | 'Pro'
}

/**
 * A notify recipient plus its current checkbox state.
 */
export interface NoteRecipientOption extends JobNoteRecipient {
  selected: boolean
}

/**
 * Dialog for adding a note to a job.
 */
export default class AddJobNoteDialogViewModel extends ModalViewModel {
  @observable composerText = ''

  /**
   * Public IDs of the recipients the author has chosen to notify.
   */
  private readonly selectedRecipientIds = observable.set<Id>()

  // Mirrors the body limit from job note service.
  readonly MAX_NOTE_LENGTH = 2000

  constructor(
    private readonly createNote: (
      body: string,
      recipientUserPublicIds: Id[],
    ) => Promise<unknown>,
    private readonly newAlert: AlertFactory,
    private readonly getRecipients: () => JobNoteRecipient[],
    closingSignal?: ModalViewModelClosingSignal,
  ) {
    super(closingSignal)
    makeObservable(this)
  }

  /**
   * Whether the note can be sent.
   */
  @computed
  get canSend() {
    return this.composerText.trim().length > 0 && !this.sendNote.pending
  }

  /**
   * The participants the author may notify, with their checkbox state.
   */
  @computed
  get recipients(): NoteRecipientOption[] {
    return this.getRecipients().map((recipient) => ({
      ...recipient,
      selected: this.selectedRecipientIds.has(recipient.userPublicId),
    }))
  }

  /**
   * Characters remaining before the note hits the max length.
   */
  @computed
  get noteCharsRemaining() {
    return this.composerText
      ? this.MAX_NOTE_LENGTH - this.composerText.length
      : this.MAX_NOTE_LENGTH
  }

  /**
   * Shows the dialog with a fresh composer.
   */
  show() {
    if (this.showing) {
      return
    }

    this.composerText = ''
    this.selectedRecipientIds.clear()
    super.show()
  }

  @action.bound
  setComposerText(text: string) {
    this.composerText = text
  }

  @action.bound
  toggleRecipient(userPublicId: Id, selected: boolean) {
    if (selected) {
      this.selectedRecipientIds.add(userPublicId)
    } else {
      this.selectedRecipientIds.delete(userPublicId)
    }
  }

  /**
   * Creates a note and closes the dialog.
   */
  sendNote = task.resolved(async () => {
    const text = this.composerText.trim()
    if (!text) {
      return
    }

    try {
      await this.createNote(text, [...this.selectedRecipientIds])
      this.close()
    } catch (err) {
      this.newAlert().fromError(err).show()
    }
  })
}
